import { useState, useRef, useEffect } from "react";
import { LuLogOut, LuUser, LuChevronDown } from "react-icons/lu";
import { MdDashboard } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import useAuth from "../useAuth";

export default function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const handleLogout = () => {
    if (window.confirm("Are you sure you want to logout?")) {
      logout();
    }
  };

  // Get user initials for avatar
  const getInitials = () => {
    const firstName = user?.firstName || "";
    const lastName = user?.lastName || "";
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="sticky top-0 z-40 bg-white/95 backdrop-blur-md border-b border-slate-200/60 shadow-sm">
      <div className="flex items-center justify-between px-6 py-3">
        {/* Left Section - Brand/Title */}
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-white border-2 border-slate-300 flex items-center justify-center shadow-md">
              <MdDashboard className="text-slate-700 text-lg" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-slate-800 tracking-tight">Dashboard</h1>
              <p className="text-xs text-slate-500">Auction Management System</p>
            </div>
          </div>
        </div>

        {/* Right Section - User Profile & Actions */}
        <div className="flex items-center gap-4">
          {/* Profile Dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="shadow-md shadow-slate-400 flex items-center gap-3 px-3 py-2 rounded-xl bg-slate-50 md:bg-white border border-slate-200/50 hover:bg-slate-100 transition-all duration-200 group"
            >
              {/* User Avatar */}
              <div className="relative">
                <div className="w-10 h-10 rounded-full bg-white border-2 border-slate-300 flex items-center justify-center shadow-md ring-2 ring-white group-hover:ring-slate-100 transition-all">
                  <span className="text-slate-700 font-bold text-sm">
                    {getInitials()}
                  </span>
                </div>
              </div>
              {/* User Info */}
              <div className="text-left hidden md:block">
                <p className="text-sm font-semibold text-slate-800">
                  {user?.data?.name}
                </p>
                {/* <p className="text-xs text-slate-500">{user?.name || "User"}</p> */}
              </div>

              {/* Dropdown Icon */}
              <LuChevronDown
                className={`text-slate-400 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}
              />
            </button>

            {/* Dropdown Menu */}
            {isDropdownOpen && (
              <div className="absolute right-0 mt-2 w-72 bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                {/* User Info Section */}
                <div className="p-4 bg-white border-b border-slate-200">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-white border-2 border-slate-300 flex items-center justify-center shadow-lg">
                      <span className="text-slate-700 font-bold text-base">
                        {getInitials()}
                      </span>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-slate-800">
                        {user?.data?.name}
                      </h3>
                      <p className="text-xs text-slate-600">{user?.data?.userName}</p>
                    </div>
                  </div>
                  <div className="mt-3 flex gap-2">
                    <span className="uppercase inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-600 text-white border border-blue-700">
                      ROLE : {user?.data?.role || "---"}
                    </span>
                    {user?.department && (
                      <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-slate-100 text-slate-700 border border-slate-200">
                        DEPARTMENT: {user?.department}
                      </span>
                    )}
                  </div>
                </div>

                {/* Menu Items */}
                <div className="py-2">
                  <button
                    onClick={() => {
                      navigate("/my/profile");
                      setIsDropdownOpen(false);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-slate-50 transition-colors text-left"
                  >
                    <LuUser className="text-slate-500 text-lg" />
                    <div>
                      <p className="text-sm font-medium text-slate-700">View Profile</p>
                      <p className="text-xs text-slate-500">Manage your account</p>
                    </div>
                  </button>

                  <div className="my-1 h-px bg-slate-200"></div>

                  <button
                    onClick={() => {
                      handleLogout();
                      setIsDropdownOpen(false);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-red-50 transition-colors text-left group"
                  >
                    <LuLogOut className="text-slate-500 group-hover:text-red-600 text-lg transition-colors" />
                    <div>
                      <p className="text-sm font-medium text-slate-700 group-hover:text-red-600 transition-colors">Logout</p>
                      <p className="text-xs text-slate-500">Sign out of your account</p>
                    </div>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
